import React, { ReactElement, useState, useEffect, Fragment } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import './Steps.scss';
import { Step } from './Types';
import { MAX_TASK_STEP_COUNT } from '@Constants/constants';
import Button from '@Common/Button';
import InputText from '@Common/InputText';
import Icon from '@Common/Icon';

interface StepsProps {
    steps: Step[];
    onStepsUpdate?: (steps: Step[]) => void;
    onStepUpdate?: (step: Step) => void;
    isReadOnly?: boolean;
    locationTaskId?: number;
}

const Steps = ({ steps = [], onStepsUpdate, onStepUpdate, isReadOnly = false, locationTaskId }: StepsProps): ReactElement => {
    const [localSteps, setLocalSteps] = useState<Step[]>(steps);
    const [isExpanded, setIsExpanded] = useState<boolean>(true);

    useEffect(() => {
        // Don't overwrite local state while a step is being actively edited
        if (!localSteps.some(s => s.isEditing)) {
            setLocalSteps(steps);
        }
    }, [steps]);

    const handleStepToggle = (stepId: number) => {
        const updatedSteps = localSteps.map(step => {
            if (step.id === stepId) {
                const updatedStep = { ...step, isCompleted: !step.isCompleted };
                if (onStepUpdate) {
                    onStepUpdate(updatedStep);
                }
                return updatedStep;
            }
            return step;
        });
        setLocalSteps(updatedSteps);
        if (onStepsUpdate) {
            onStepsUpdate(updatedSteps);
        }
    };

    const completedSteps = localSteps.filter(step => step.isCompleted).length;
    const totalSteps = localSteps.length;

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        const steps = Array.from(localSteps);
        const [removed] = steps.splice(result.source.index, 1);
        steps.splice(result.destination.index, 0, removed);

        // Update ordinals
        for (let i = 0; i < steps.length; i++) {
            steps[i].ordinal = i + 1;
        }

        setLocalSteps(steps);
        if (onStepsUpdate) {
            onStepsUpdate(steps);
        }
    };

    const onUpdateStepName = (step: Step, e: React.ChangeEvent<HTMLInputElement>) => {
        const updatedSteps = localSteps.map(s =>
            s.id === step.id ? { ...s, name: e.target.value } : s
        );
        setLocalSteps(updatedSteps);
        if (onStepsUpdate) {
            onStepsUpdate(updatedSteps);
        }
    };

    const toggleStepEdit = (step: Step) => {
        const updatedSteps = localSteps.map(s =>
            s.id === step.id ? { ...s, isEditing: !s.isEditing } : s
        );
        setLocalSteps(updatedSteps);
        if (onStepsUpdate) {
            onStepsUpdate(updatedSteps);
        }
    };

    const onFinishEditingStep = (step: Step, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            toggleStepEdit(step);
        }
    };

    const onAddStep = () => {
        // Generate unique ID for new steps using negative numbers
        const existingNewStepIds = localSteps
            .filter(s => s.id <= 0)
            .map(s => s.id);
        const newStepId = existingNewStepIds.length > 0
            ? Math.min(...existingNewStepIds) - 1
            : -1;

        const newStep: Step = {
            id: newStepId,
            locationTaskId: locationTaskId,
            name: '',
            isCompleted: false,
            ordinal: localSteps.length + 1,
            isEditing: true,
            isActive: true
        };

        const updatedSteps = [...localSteps, newStep];
        setLocalSteps(updatedSteps);
        if (onStepsUpdate) {
            onStepsUpdate(updatedSteps);
        }
    };

    const onRemoveStep = (step: Step) => {
        const updatedSteps = localSteps.filter(s => s.id !== step.id);

        // Update ordinals for remaining steps
        for (let i = 0; i < updatedSteps.length; i++) {
            updatedSteps[i].ordinal = i + 1;
        }

        setLocalSteps(updatedSteps);
        if (onStepsUpdate) {
            onStepsUpdate(updatedSteps);
        }
    };

    const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
        userSelect: 'none',
        background: isDragging ? '#f1f5f9' : 'white',
        border: isDragging ? '2px solid #3b82f6' : '1px solid transparent',
        borderRadius: '6px',
        ...draggableStyle,
    });

    const getListStyle = (isDraggingOver: boolean) => ({
        background: isDraggingOver ? '#f8fafc' : 'transparent',
        borderRadius: '8px',
        padding: '4px',
    });

    const canManageSteps = !isReadOnly;
    const canAddMoreSteps = canManageSteps && localSteps.length < MAX_TASK_STEP_COUNT;

    return (
        <div className="location-task-steps" data-component="steps">
            {/* Collapsible Header */}
            <div className="card-header d-flex justify-content-between align-items-center mb-3">
                <button
                    type="button"
                    className="card-title mb-0 btn btn-link p-0 text-start"
                    onClick={() => setIsExpanded(!isExpanded)}
                    aria-expanded={isExpanded}
                    style={{ cursor: 'pointer', userSelect: 'none', textDecoration: 'none' }}
                >
                    <i className={`far ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'} me-2`}></i>
                    Task Steps
                </button>
                {isExpanded && canAddMoreSteps && (
                    <div className={`link-with-icon ${localSteps.length >= MAX_TASK_STEP_COUNT ? 'disabled' : ''}`}>
                        <i className={'far fa-circle-plus'}></i>
                        <button disabled={!canAddMoreSteps} type='button' title="Add Step" className="btn btn-link" onClick={onAddStep}>
                            Add Step
                        </button>
                    </div>
                )}
            </div>

            {isExpanded && (
                <div className='card-body'>

                    {/* Progress Bar */}
                    {totalSteps > 0 && (
                        <div className="steps-progress mb-3">
                            <div className="text-center mb-2">
                                <span style={{
                                    fontFamily: 'Greycliff',
                                    fontSize: '12px',
                                    lineHeight: '16px',
                                    fontWeight: '400',
                                    color: '#6c757d'
                                }}>{completedSteps} of {totalSteps} done</span>
                            </div>
                            <div className="progress" style={{ height: '6px' }}>
                                <div
                                    className="progress-bar bg-success"
                                    role="progressbar"
                                    style={{ width: `${totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0}%` }}
                                    aria-valuenow={completedSteps}
                                    aria-valuemin={0}
                                    aria-valuemax={totalSteps}>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Steps List */}
                    {totalSteps > 0 ? (
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="steps-droppable">
                                {(provided, snapshot) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className="steps-list"
                                        style={getListStyle(snapshot.isDraggingOver)}
                                    >
                                        {localSteps.map((step, index) => {
                                            const isFromBaseline = step.baselineTaskStepId && step.baselineTaskStepId > 0;
                                            const isDragDisabled = isReadOnly || (isFromBaseline && !step.isEditing);

                                            return (
                                                <Draggable
                                                    key={`step-${step.id}-${index}`}
                                                    draggableId={`step-${step.id}-${index}`}
                                                    index={index}
                                                    isDragDisabled={isDragDisabled}
                                                >
                                                    {(provided, snapshot) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            className={`step-item d-flex align-items-center justify-content-center gap-2 p-1 mb-2 ${step.isCompleted ? 'completed' : ''} ${step.isEditing ? 'editing' : ''}`}
                                                            style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                                                        >
                                                            {/* Drag Handle - Only show when editing */}
                                                            {!isReadOnly && step.isEditing && (
                                                                <div {...provided.dragHandleProps} className="drag-handle me-2">
                                                                    <Icon name="bars" stylePrefix="fas" />
                                                                </div>
                                                            )}

                                                            {/* Checkbox */}
                                                            <div
                                                                className="step-checkbox"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleStepToggle(step.id);
                                                                }}
                                                            >
                                                                <div className={`form-check-input ${step.isCompleted ? 'checked' : ''}`}>
                                                                    {step.isCompleted && <i className="fas fa-check text-white" style={{ fontSize: '10px' }}></i>}
                                                                </div>
                                                            </div>

                                                            {/* Step Content */}
                                                            <div className="step-content flex-grow-1">
                                                                {step.isEditing ? (
                                                                    <InputText
                                                                        name="stepname"
                                                                        type="text"
                                                                        placeholder="Enter step name"
                                                                        value={step.name || ''}
                                                                        onChange={(e) => onUpdateStepName(step, e)}
                                                                        onKeyDown={(e) => onFinishEditingStep(step, e)}
                                                                        maxLength={100}
                                                                        className="mb-0"
                                                                    />
                                                                ) : (
                                                                    <div
                                                                        className={`step-name ${step.isCompleted ? 'text-decoration-line-through text-muted' : ''}`}
                                                                        style={{
                                                                            wordBreak: 'break-word',
                                                                            whiteSpace: 'pre-wrap',
                                                                            lineHeight: '1.4'
                                                                        }}
                                                                    >
                                                                        {step.name || step.description || 'Unnamed Step'}
                                                                        {isFromBaseline && (
                                                                            <small className="text-muted ms-2">(from baseline)</small>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Action Buttons */}
                                                            {canManageSteps && (
                                                                <div className="step-actions d-flex gap-2">
                                                                    {/* Edit Button - Only for location-level steps or all steps when editing is allowed */}
                                                                    {!isFromBaseline && (
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-link p-0"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                toggleStepEdit(step);
                                                                            }}
                                                                            title={step.isEditing ? 'Save changes' : 'Edit step'}
                                                                        >
                                                                            <Icon name={step.isEditing ? 'check' : 'pencil-alt'} stylePrefix="fas" />
                                                                        </button>
                                                                    )}

                                                                    {/* Delete Button - Only for location-level steps */}
                                                                    {!isFromBaseline && !step.isEditing && (
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-link p-0 text-danger"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                onRemoveStep(step);
                                                                            }}
                                                                            title="Delete step"
                                                                        >
                                                                            <Icon name="trash" stylePrefix="fas" />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    ) : (
                        <div className="text-center text-muted py-2">
                            <i className="fas fa-list-check mb-2" style={{ fontSize: '18px', opacity: 0.3 }}></i>
                            <div style={{ fontSize: '14px', marginBottom: '8px' }}>No steps available for this task</div>
                        </div>
                    )}

                    {/* Step limit warning */}
                    {localSteps.length >= MAX_TASK_STEP_COUNT && (
                        <small className="text-muted d-block text-center mt-2">
                            Maximum of {MAX_TASK_STEP_COUNT} steps allowed
                        </small>
                    )}
                </div>
            )}
        </div>
    );
};

export default Steps;